import {
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CustomNumberInput from "components/CustomNumberInput";
import { useSession } from "providers/SessionProvider";
import { useMemo, useState } from "react";
import { AdditionalItem, Client, ClientAdditionalItem } from "types";

const REQUIRED_MOVEMENT_TYPE_OPTIONS = [
  { value: "income", label: "Ingreso" },
  { value: "discharge", label: "Egreso" },
] as const;

export default function ClientAdditionalItemsPreferences({
  client,
  onChange,
}: {
  client: Client;
  onChange: <K extends keyof Omit<Client, "id">>(
    name: K,
    value: Omit<Client, "id">[K],
  ) => void;
}) {
  const { additionalItems, clients } = useSession();
  const [search, setSearch] = useState("");

  const scopedClientIds = [
    client.id,
    ...clients
      .filter((item) => item.parent_client_id === client.id)
      .map((item) => item.id),
  ];

  const availableAdditionalItems = additionalItems.filter(
    (item) => item.client_id == null || scopedClientIds.includes(item.client_id),
  );

  const filteredAdditionalItems = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) return availableAdditionalItems;

    return availableAdditionalItems.filter((item) =>
      item.description.toLowerCase().includes(normalizedSearch),
    );
  }, [availableAdditionalItems, search]);

  const handlePriceChange = (additionalItemId: number, value: string) => {
    const price = value === "" ? null : Number(value);
    const currentItems = client.client_additional_items ?? [];

    const exists = currentItems.some(
      (item) => item.additional_item_id === additionalItemId,
    );

    const nextItems: ClientAdditionalItem[] = exists
      ? currentItems.map((item) =>
          item.additional_item_id === additionalItemId
            ? { ...item, price }
            : item,
        )
      : [
          ...currentItems,
          {
            additional_item_id: additionalItemId,
            price,
          },
        ];

    onChange("client_additional_items", nextItems);
  };

  const handleRequiredChange = (
    additionalItemId: number,
    isRequired: boolean,
  ) => {
    const currentItems = client.client_additional_items ?? [];

    const exists = currentItems.some(
      (item) => item.additional_item_id === additionalItemId,
    );

    const nextItems: ClientAdditionalItem[] = exists
      ? currentItems.map((item) =>
          item.additional_item_id === additionalItemId
            ? {
                ...item,
                is_required: isRequired,
                required_movement_types: isRequired
                  ? item.required_movement_types ?? ["income", "discharge"]
                  : null,
              }
            : item,
        )
      : [
          ...currentItems,
          {
            additional_item_id: additionalItemId,
            price: null,
            is_required: isRequired,
            required_movement_types: isRequired
              ? ["income", "discharge"]
              : null,
          },
        ];

    onChange("client_additional_items", nextItems);
  };

  const handleRequiredMovementTypesChange = (
    additionalItemId: number,
    event: SelectChangeEvent<("income" | "discharge")[]>,
  ) => {
    const movementTypes = event.target.value;
    const requiredMovementTypes =
      typeof movementTypes === "string"
        ? movementTypes.split(",")
        : movementTypes;
    const currentItems = client.client_additional_items ?? [];

    const exists = currentItems.some(
      (item) => item.additional_item_id === additionalItemId,
    );

    const nextItems: ClientAdditionalItem[] = exists
      ? currentItems.map((item) =>
          item.additional_item_id === additionalItemId
            ? {
                ...item,
                is_required: true,
                required_movement_types: requiredMovementTypes as (
                  | "income"
                  | "discharge"
                )[],
              }
            : item,
        )
      : [
          ...currentItems,
          {
            additional_item_id: additionalItemId,
            price: null,
            is_required: true,
            required_movement_types: requiredMovementTypes as (
              | "income"
              | "discharge"
            )[],
          },
        ];

    onChange("client_additional_items", nextItems);
  };

  const getClientPrice = (additionalItem: AdditionalItem) =>
    client.client_additional_items?.find(
      (item) => item.additional_item_id === additionalItem.id,
    )?.price ?? "";

  const getIsRequired = (additionalItem: AdditionalItem) =>
    client.client_additional_items?.find(
      (item) => item.additional_item_id === additionalItem.id,
    )?.is_required === true;

  const getRequiredMovementTypes = (additionalItem: AdditionalItem) =>
    client.client_additional_items?.find(
      (item) => item.additional_item_id === additionalItem.id,
    )?.required_movement_types ?? [];

  return (
    <Paper elevation={4}>
      <Grid item xs={12} display="flex" justifyContent="center" py={1}>
        <Typography variant="h5">Items Adicionales</Typography>
      </Grid>
      <Grid item xs={12} px={2} pb={1}>
        <TextField
          fullWidth
          size="small"
          label="Buscar adicional"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Grid>
      <Grid item xs={12} p={2} pt={1}>
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 350,
            overflow: "auto",
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "35%" }}>Adicional</TableCell>
                <TableCell sx={{ width: "10%" }}>Precio base</TableCell>
                <TableCell sx={{ width: "15%" }}>Precio cliente</TableCell>
                <TableCell sx={{ width: "10%" }}>Obligatorio</TableCell>
                <TableCell sx={{ width: "30%" }}>Tipo de movimiento</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAdditionalItems.map((additionalItem) => {
                const isRequired = getIsRequired(additionalItem);

                return (
                  <TableRow key={additionalItem.id}>
                    <TableCell>{additionalItem.description}</TableCell>
                    <TableCell>{additionalItem.price}</TableCell>
                    <TableCell>
                      <CustomNumberInput
                        label="$"
                        value={getClientPrice(additionalItem)}
                        onChange={(e) =>
                          handlePriceChange(additionalItem.id, e.target.value)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Checkbox
                        checked={isRequired}
                        onChange={(e) =>
                          handleRequiredChange(
                            additionalItem.id,
                            e.target.checked,
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small" disabled={!isRequired}>
                        <InputLabel>Movimiento</InputLabel>
                        <Select
                          multiple
                          label="Movimiento"
                          value={getRequiredMovementTypes(additionalItem)}
                          onChange={(event) =>
                            handleRequiredMovementTypesChange(
                              additionalItem.id,
                              event,
                            )
                          }
                          renderValue={(selected) =>
                            REQUIRED_MOVEMENT_TYPE_OPTIONS
                              .filter((option) =>
                                selected.includes(option.value),
                              )
                              .map((option) => option.label)
                              .join(", ")
                          }
                        >
                          {REQUIRED_MOVEMENT_TYPE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Checkbox
                                checked={getRequiredMovementTypes(
                                  additionalItem,
                                ).includes(option.value)}
                              />
                              <ListItemText primary={option.label} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Paper>
  );
}
